import lendmarqLogo from "../../../assets/logos/lendmarq-logo.png";

/**
 * The letterhead logo for generated submission PDFs.
 *
 * jsPDF's `addImage` needs the bytes up front — it can't fetch a URL — so the
 * asset has to be resolved to a data URL before the document is built. That is
 * asynchronous, which is why the three entry points in submissionPdf.js are.
 * The result is cached for the life of the tab: the same logo goes on every
 * submission, and re-reading it per download would be wasted work.
 *
 * LENDMARQ DIVERGENCE: the asset comes from this app's own `src/assets/logos`
 * (the same wordmark the dashboard and the public forms carry) rather than from
 * anything inside the vendored `src/tpo` tree — same pattern as BrandLogo.
 *
 * Callers can override or suppress it:
 *   setSubmissionPdfLogo(dataUrl)  — use this image instead
 *   setSubmissionPdfLogo(null)     — no letterhead at all
 *   downloadSubmissionPdf(row, { logo })  — per-document, wins over both
 */
let override;
let pending = null;

export const setSubmissionPdfLogo = (dataUrl) => {
  override = dataUrl;
  pending = null;
};

// Base64 by hand rather than via FileReader: this runs identically in a browser
// and in a plain node harness, which is what makes the generated document
// testable outside the app.
const toDataUrl = (bytes, mime) => {
  let binary = "";
  const CHUNK = 0x8000; // fromCharCode has an argument-count ceiling
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return `data:${mime};base64,${btoa(binary)}`;
};

/**
 * Resolves to the data URL, or `null` when there's no usable logo — a missing
 * or unreadable asset must degrade to a document without a letterhead, never to
 * a failed download.
 */
export const loadSubmissionPdfLogo = () => {
  if (override !== undefined) return Promise.resolve(override);
  if (pending) return pending;

  pending = (async () => {
    try {
      const res = await fetch(lendmarqLogo);
      if (!res.ok) return null;
      const bytes = new Uint8Array(await res.arrayBuffer());
      if (!bytes.length) return null;
      return toDataUrl(
        bytes,
        res.headers.get("content-type") || "image/png",
      );
    } catch (e) {
      return null;
    }
  })();

  return pending;
};

export default loadSubmissionPdfLogo;
